// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Rafael Pereira Satyro

import { useEffect, useState } from 'react';
import { Modal } from '@loa/ui-components';
import { TiptapEditor } from '../../invitation/TiptapEditor';
import { noteApi } from '../../../api/noteApi';
import type { Note } from '../../../types/note';
import './NoteFormModal.css';

type NoteFormModalProps = Readonly<{
  open: boolean;
  profileId: string;
  note?: Note | null;
  onClose: () => void;
  onSaved: () => void;
}>;

// Extract plain text via the DOM parser rather than a tag-stripping regex.
// A single-pass regex is incomplete sanitization (nested/overlapping tags can
// reconstruct markup); DOMParser.textContent is robust. This is only used for
// the "is there any text?" save guard below — the HTML itself is produced by
// the Tiptap editor and persisted as-is.
const stripHtml = (html: string): string =>
  (new DOMParser().parseFromString(html, 'text/html').body.textContent ?? '').trim();

export function NoteFormModal({ open, profileId, note, onClose, onSaved }: NoteFormModalProps) {
  const isEdit = Boolean(note);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setTitle(note?.title ?? '');
      setContent(note?.content ?? '');
      setError(null);
    }
  }, [open, note]);

  const canSave = title.trim().length > 0 && stripHtml(content).length > 0 && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    try {
      setSaving(true);
      setError(null);
      const payload = { title: title.trim(), content };
      if (isEdit && note) {
        await noteApi.updateRelationshipNote(profileId, note.id, payload);
      } else {
        await noteApi.createRelationshipNote(profileId, payload);
      }
      onSaved();
    } catch (err) {
      const apiError = err as { response?: { data?: { message?: string } } };
      setError(apiError.response?.data?.message || 'Failed to save note. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose} size="xlarge">
      <Modal.Header title={isEdit ? 'Edit Note' : 'New Note'} onClose={handleClose} />
      <Modal.Content className="!p-0">
        <div className="note-form">
          <div className="note-form__field">
            <label htmlFor="note-title" className="note-form__label">Note title</label>
            <input
              id="note-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={255}
              disabled={saving}
              className="note-form__title-input"
            />
          </div>

          <div className="note-form__editor">
            <TiptapEditor
              content={content}
              onChange={setContent}
              placeholder="Write your note here..."
              disabled={saving}
              minHeight="200px"
              ariaLabel="Note content"
            />
          </div>

          {error && (
            <div className="note-form__error">
              {error}
            </div>
          )}
        </div>
      </Modal.Content>
      <Modal.Actions
        secondaryAction={{ label: 'Cancel', onClick: handleClose, disabled: saving }}
        primaryAction={{ label: 'Save Note', onClick: () => { void handleSave(); }, loading: saving, disabled: !canSave }}
      />
    </Modal>
  );
}
